import { x25519 } from '@noble/curves/ed25519.js';

import { randomSeed } from '../utils';

// PKCS8 wrapper for X25519 private keys: fixed 16-byte ASN.1 prefix + 32-byte scalar.
// Differs from the Ed25519 prefix only in the OID byte (0x6e vs 0x70).
export const X25519_PKCS8_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x6e, 0x04, 0x22, 0x04, 0x20,
]);

export const X25519_KEY_LENGTH = 32;

export type OpaqueX25519Key = {
  data: Uint8Array<ArrayBuffer>;
};

export async function importX25519PrivKey(
  scalar: Uint8Array<ArrayBuffer>,
): Promise<OpaqueX25519Key> {
  return { data: scalar };
}

export async function importX25519PubKey(raw: Uint8Array<ArrayBuffer>): Promise<OpaqueX25519Key> {
  return { data: raw };
}

export async function exportX25519PrivKey(
  privKey: OpaqueX25519Key,
): Promise<Uint8Array<ArrayBuffer>> {
  return privKey.data;
}

export async function exportX25519PubKey(
  pubKey: OpaqueX25519Key,
): Promise<Uint8Array<ArrayBuffer>> {
  return pubKey.data;
}

export async function exportX25519PubKeyBytes(
  privKey: OpaqueX25519Key,
): Promise<Uint8Array<ArrayBuffer>> {
  return x25519.scalarMultBase(privKey.data);
}

export async function x25519DeriveShared(
  privateKey: OpaqueX25519Key,
  publicKey: OpaqueX25519Key,
): Promise<Uint8Array<ArrayBuffer>> {
  return x25519.getSharedSecret(privateKey.data, publicKey.data);
}

export interface X25519KeyPair {
  privateKey: OpaqueX25519Key;
  publicKey: OpaqueX25519Key;
}

export interface X25519RawKeyPair {
  privKey: Uint8Array<ArrayBuffer>;
  pubKey: Uint8Array<ArrayBuffer>;
}

export async function newX25519KeyPair(): Promise<X25519KeyPair> {
  const privateKey = { data: randomSeed() };
  const publicKey = { data: await exportX25519PubKeyBytes(privateKey) };
  return { privateKey, publicKey };
}
